import { toFiniteNumber } from "../blueprint/runtime/helpers.js";
import { buildLayoutListScopedRowData } from "../layout/layout-row-binding-fingerprint.js";
import { coerceToBoolean } from "./helpers.js";
import type {
  CompiledListFilterRule,
  CompiledListSortRule,
  RuntimeContext,
} from "./types.js";

type IndexedListRow = {
  item: unknown;
  originalIndex: number;
};

function buildListSortComparatorContext(
  ctx: RuntimeContext,
  sortAIndex: number,
  sortBIndex: number,
  rows: unknown[],
  listSourceKey?: string,
): RuntimeContext {
  const a = Math.max(0, Math.round(sortAIndex));
  const b = Math.max(0, Math.round(sortBIndex));
  const scoped: RuntimeContext = {
    ...ctx,
    sortAIndex: a,
    sortBIndex: b,
    iterationItemA: rows[a],
    iterationItemB: rows[b],
  };

  if (listSourceKey) {
    (scoped as Record<string, unknown>)[listSourceKey] = rows;
  }

  return scoped;
}

/**
 * Applies compiled `setFilter` rules; indices refer to the full bound list.
 */
export function applyListFilterRules(
  rows: unknown[],
  ctx: RuntimeContext,
  rules: CompiledListFilterRule[],
  listSourceKey?: string,
): IndexedListRow[] {
  const indexed = rows.map((item, originalIndex) => ({ item, originalIndex }));

  if (!rules.length) {
    return indexed;
  }

  return indexed.filter(({ item, originalIndex }) => {
    const rowCtx = buildLayoutListScopedRowData(ctx, {
      rowIndex: originalIndex,
      rowItem: item,
      listSourceKey,
      rowsListData: rows,
    });

    return rules.every((rule) =>
      coerceToBoolean(rule.condition.evaluate(rowCtx)),
    );
  });
}

/**
 * Applies compiled `setSort` rules with stable tie-break on original index.
 */
export function applyListSortRules(
  indexedRows: IndexedListRow[],
  ctx: RuntimeContext,
  rules: CompiledListSortRule[],
  rows: unknown[],
  listSourceKey?: string,
): IndexedListRow[] {
  if (!rules.length) {
    return indexedRows;
  }

  let sorted = [...indexedRows];

  for (const rule of rules) {
    sorted.sort((left, right) => {
      const comparatorCtx = buildListSortComparatorContext(
        ctx,
        left.originalIndex,
        right.originalIndex,
        rows,
        listSourceKey,
      );

      const compared = toFiniteNumber(
        rule.comparator.evaluate(comparatorCtx),
        NaN,
      );

      if (Number.isFinite(compared) && compared !== 0) {
        return compared;
      }

      return left.originalIndex - right.originalIndex;
    });
  }

  return sorted;
}

/**
 * Filter, then sort, preserving original indices into the bound list.
 */
export function transformLayoutListRows(
  rawRows: unknown[],
  ctx: RuntimeContext,
  filterRules: CompiledListFilterRule[],
  sortRules: CompiledListSortRule[],
  listSourceKey?: string,
): IndexedListRow[] {
  const filtered = applyListFilterRules(
    rawRows,
    ctx,
    filterRules,
    listSourceKey,
  );

  return applyListSortRules(filtered, ctx, sortRules, rawRows, listSourceKey);
}
